import {
  Button,
  Card,
  Checkbox,
  Input,
  InputSelect,
  Label,
} from '@features/ui';
import { EditorComponent } from '@features/admin/emails/EditorComponent';
import { FC, useCallback, useEffect, useState } from 'react';
import { KnowledgeBaseFormData, schemaKnowledgeBase, ContentTypeItem } from '@src/types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getKnowledgeBaseTypes } from '@src/services/knowledgeBase';
import { KnowledgeBaseTypePopup } from '@features/admin/knowledgebase/KnowledgeBaseTypePopup';

interface EmailDetailProps {
  isEdit?: boolean;
}

export const KnowledgeBaseEdit: FC<EmailDetailProps> = () => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<KnowledgeBaseFormData>({
    resolver: yupResolver(schemaKnowledgeBase),
  });
  const params = useParams();
  const navigate = useNavigate();
  const [isEdit] = useState(!!params?.id);
  const [typeOptions, setTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [isTypePopupOpen, setIsTypePopupOpen] = useState(false);

  const loadTypes = useCallback(async () => {
    try {
      const res = await getKnowledgeBaseTypes();
      const sorted = [...(res.data ?? [])].sort(
        (a: ContentTypeItem, b: ContentTypeItem) =>
          (a.sort_order ?? 999) - (b.sort_order ?? 999)
      );

      setTypeOptions(sorted.map((t: ContentTypeItem) => ({ value: String(t.id), label: t.name })));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  useEffect(() => {
    if (!isEdit || !params?.id) return;
    let mounted = true;

    const load = async () => {
      try {
        const id = Number(params.id);

        if (!id) return;
        await loadTypes();
        const res = await (
          await import('@src/services/knowledgeBase')
        ).getKnowledgeBaseDetail(id);
        const d = res.data;

        if (!mounted || !d) return;
        reset({
          title: d.name ?? '',
          description: d.content ?? '',
          is_published: !!d.is_published,
          type_id: d.type_id,
        });
      } catch {
        // ignore
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [isEdit, loadTypes, params?.id, reset]);

  const handleSubmitForm = useCallback(
    async (data: KnowledgeBaseFormData) => {
      try {
        const payload = {
          name: data.title,
          content: data.description,
          type_id: data.type_id,
          is_published: !!data.is_published,
        };

        if (isEdit && params?.id) {
          const id = Number(params.id);

          if (id) {
            const { updateKnowledgeBase } = await import('@src/services/knowledgeBase');

            await updateKnowledgeBase(id, payload);
            toast.success('Updated successfully');
            navigate('/admin/knowledge-base');

            return;
          }
        }

        const { createKnowledgeBase } = await import('@src/services/knowledgeBase');

        await createKnowledgeBase(payload);
        toast.success('Created successfully');
        navigate(`/admin/knowledge-base`);
      } catch {
        toast.error('Something went wrong. Please try again later.');
      }
    },
    [isEdit, navigate, params.id]
  );

  return (
    <>
      <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
        <div className={'flex justify-between items-center'}>
          <div className={'flex gap-x-2 text-[23px]'}>
            <p>{`${isEdit ? 'Edit' : 'Create'} Knowledge Base Article`}</p>
          </div>
          <div className={'flex items-center gap-x-2'}>
            {isEdit ? (
              <Button
                className={'rounded-[2px]'}
                size={'sm'}
                variant={'destructive'}
                onClick={async () => {
                  try {
                    const confirmed = window.confirm(
                      'Are you sure you want to delete this article?'
                    );

                    if (!confirmed) return;
                    const id = Number(params.id);

                    if (!id) return;
                    const { deleteKnowledgeBase } = await import(
                      '@src/services/knowledgeBase'
                    );

                    await deleteKnowledgeBase(id);
                    toast.success('Deleted successfully');
                    navigate('/admin/knowledge-base');
                  } catch {
                    toast.error('Unable to delete. Please try again later.');
                  }
                }}
              >
                Delete
              </Button>
            ) : null}
            <Button
              className={'rounded-[2px]'}
              loading={isSubmitting}
              size={'sm'}
              type={'submit'}
              variant={'lime'}
            >
              Save
            </Button>
          </div>
        </div>
        <Card className={'mt-4.5 mb-5 text-xs space-y-7'}>
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <div className={'flex flex-col gap-y-1'}>
                <Label className={'text-xs mb-1'}>Title</Label>
                <Input
                  placeholder={'Enter'}
                  textSize={'xs'}
                  variant={'secondary'}
                  {...field}
                />
                <p className={'text-red-500'}>{errors.title?.message}</p>
              </div>
            )}
          />
          <div className={'flex flex-col gap-y-1'}>
            <div className="flex items-center justify-between mb-1">
              <Label className={'text-xs'}>Type</Label>
              <Button
                className="text-xs h-7"
                size="sm"
                type="button"
                variant="outline"
                onClick={() => setIsTypePopupOpen(true)}
              >
                Edit Types
              </Button>
            </div>
            <Controller
              control={control}
              name="type_id"
              render={({ field }) => (
                <InputSelect
                  options={typeOptions}
                  placeholder={'Select type'}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={(val) => field.onChange(val ? Number(val) : undefined)}
                />
              )}
            />
            <p className={'text-red-500'}>{errors.type_id?.message}</p>
          </div>
        </Card>
        <Card className={'flex flex-col gap-y-5.5'}>
          <p className={'text-sm'}>Content</p>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <div className={'flex flex-col gap-y-1'}>
                <EditorComponent {...field} />
                {errors.description?.message ? (
                  <p className={'text-red-500 text-xs'}>
                    {errors.description?.message}
                  </p>
                ) : null}
              </div>
            )}
          />
          <Controller
            control={control}
            name="is_published"
            render={({ field }) => (
              <div className={'text-xs flex flex-col gap-y-2.5 font-semibold'}>
                <p>Published ?</p>
                <div className={'flex items-center gap-x-3'}>
                  <Checkbox
                    checked={!!field.value}
                    className={'border-[white] border-[1px] h-5 w-5'}
                    label={'Yes'}
                    onCheckedChange={(value) => field.onChange(value)}
                  />
                </div>
              </div>
            )}
          />
        </Card>
      </form>

      <KnowledgeBaseTypePopup
        open={isTypePopupOpen}
        onOpenChange={setIsTypePopupOpen}
        onTypesChange={loadTypes}
      />
    </>
  );
};
